import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { ArrowLeft, PencilSimple, Trash, UserPlus, Users as UsersIcon, Eye, EyeSlash } from '@phosphor-icons/react';

export default function UsersManagement() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [form, setForm] = useState({ role: '', institution_id: '', is_active: true });
  const [createForm, setCreateForm] = useState({ full_name: '', email: '', password: '', role: 'reporter', institution_id: '', phone: '' });
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [usersRes, instRes] = await Promise.all([
        authAxios().get('/admin/users'),
        authAxios().get('/institutions/all')
      ]);
      setUsers(usersRes.data);
      setInstitutions(instRes.data);
    } catch { toast.error('Veri yüklenemedi'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [authAxios]); // eslint-disable-line

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ full_name: u.full_name || '', email: u.email || '', phone: u.phone || '', role: u.role, institution_id: u.institution_id || '', is_active: u.is_active });
    setEditDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const update = { role: form.role, is_active: form.is_active, full_name: form.full_name, email: form.email };
      if (form.phone) update.phone = form.phone;
      if (form.role === 'officer' && form.institution_id) update.institution_id = form.institution_id;
      else if (form.role !== 'officer') update.institution_id = null;
      await authAxios().put(`/admin/users/${editingUser.id}`, update);
      toast.success('Kullanıcı güncellendi');
      setEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme başarısız');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await authAxios().delete(`/admin/users/${deletingUser.id}`);
      toast.success('Kullanıcı silindi');
      setDeleteDialog(false);
      setDeletingUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silme başarısız');
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!createForm.full_name || !createForm.email || !createForm.password) {
      toast.error('Ad, e-posta ve şifre zorunludur');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...createForm };
      if (payload.role !== 'officer') payload.institution_id = null;
      else payload.institution_id = payload.institution_id || null;
      if (!payload.phone) delete payload.phone;
      await authAxios().post('/admin/users', payload);
      toast.success('Kullanıcı oluşturuldu');
      setCreateDialog(false);
      setCreateForm({ full_name: '', email: '', password: '', role: 'reporter', institution_id: '', phone: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Oluşturma başarısız');
    } finally { setSaving(false); }
  };

  const roleLabels = { admin: 'Yönetici', officer: 'Memur', reporter: 'Vatandas' };
  const roleColors = { admin: 'bg-purple-600 text-white', officer: 'bg-blue-600 text-white', reporter: 'bg-[#2C2C2E] text-white' };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="bg-[#1C1C1E] border-b border-[#2C2C2E] px-4 py-3 flex items-center gap-3">
        <Button data-testid="back-button" variant="ghost" onClick={() => navigate('/admin')} className="text-white hover:bg-[#2C2C2E] h-10 w-10 p-0">
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <UsersIcon weight="fill" className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h1 className="font-heading text-lg text-white">KULLANICI YÖNETİMİ</h1>
        </div>
        <Button data-testid="create-user-button" onClick={() => setCreateDialog(true)} className="ml-auto bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white h-9 px-3 font-heading text-xs uppercase">
          <UserPlus weight="bold" className="w-4 h-4 mr-1" /> Ekle
        </Button>
      </header>

      <main className="p-4 max-w-3xl mx-auto">
        {loading ? (
          <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>
        ) : (
          <div className="space-y-3">
            {users.map(u => {
              const inst = institutions.find(i => i.id === u.institution_id);
              return (
                <Card key={u.id} className="bg-[#1C1C1E] border-[#2C2C2E]">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-white font-medium">{u.full_name}</p>
                        <Badge className={`${roleColors[u.role]} text-xs`}>{roleLabels[u.role]}</Badge>
                        {!u.is_active && <Badge className="bg-[hsl(var(--primary))] text-white text-xs">Pasif</Badge>}
                      </div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{u.email}</p>
                      {inst && <p className="text-xs text-[hsl(var(--accent))] mt-0.5">{inst.name}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        data-testid={`edit-user-${u.id}`}
                        variant="ghost"
                        onClick={() => openEdit(u)}
                        className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-9 w-9 p-0"
                      >
                        <PencilSimple weight="bold" className="w-4 h-4" />
                      </Button>
                      {u.role !== 'admin' && (
                      <Button
                        data-testid={`delete-user-${u.id}`}
                        variant="ghost"
                        onClick={() => { setDeletingUser(u); setDeleteDialog(true); }}
                        className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:bg-[#2C2C2E] h-9 w-9 p-0"
                      >
                        <Trash weight="bold" className="w-4 h-4" />
                      </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-[#1C1C1E] border-[#2C2C2E] text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">KULLANICI DÜZENLE</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">Ad Soyad</label>
                <Input data-testid="edit-fullname" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11" />
              </div>
              <div>
                <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">E-posta</label>
                <Input data-testid="edit-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11" />
              </div>
              <div>
                <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">Telefon</label>
                <Input data-testid="edit-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+905xx..." className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11" />
              </div>
              <div>
                <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Rol</label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="role-select" className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                    <SelectItem value="reporter" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Vatandas</SelectItem>
                    <SelectItem value="officer" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Memur</SelectItem>
                    <SelectItem value="admin" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Yönetici</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === 'officer' && (
                <div>
                  <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Kurum</label>
                  <Select value={form.institution_id} onValueChange={v => setForm({...form, institution_id: v})}>
                    <SelectTrigger data-testid="institution-select" className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white">
                      <SelectValue placeholder="Kurum seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                      {institutions.map(i => (
                        <SelectItem key={i.id} value={i.id} className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">{i.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Durum</label>
                <Select value={form.is_active ? 'active' : 'inactive'} onValueChange={v => setForm({...form, is_active: v === 'active'})}>
                  <SelectTrigger data-testid="status-select" className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                    <SelectItem value="active" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Aktif</SelectItem>
                    <SelectItem value="inactive" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Pasif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-user-button" onClick={handleSave} disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white h-12 font-heading uppercase">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-[#1C1C1E] border-[#2C2C2E] text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">KULLANICI SİL</DialogTitle>
          </DialogHeader>
          {deletingUser && (
            <div className="space-y-3">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                <span className="text-white font-medium">{deletingUser.full_name}</span> ({deletingUser.email}) kullanıcısını silmek istediğinize emin misiniz?
              </p>
              <p className="text-xs text-[hsl(var(--primary))]">Bu işlem geri alınamaz.</p>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button data-testid="cancel-delete-button" variant="outline" onClick={() => setDeleteDialog(false)} className="border-[#2C2C2E] text-white hover:bg-[#2C2C2E] hover:text-white h-12">
              İptal
            </Button>
            <Button data-testid="confirm-delete-button" onClick={handleDelete} disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white h-12 font-heading uppercase">
              {saving ? 'Siliniyor...' : 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="bg-[#1C1C1E] border-[#2C2C2E] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">YENİ KULLANICI</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">Ad Soyad *</label>
              <Input data-testid="create-fullname" value={createForm.full_name} onChange={e => setCreateForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Ad Soyad" className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">E-posta *</label>
              <Input data-testid="create-email" type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} placeholder="ornek@mail.com" className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">Şifre *</label>
              <div className="relative">
                <Input data-testid="create-password" type={showCreatePassword ? "text" : "password"} value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} placeholder="Min. 6 karakter" className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11 pr-12" />
                <button type="button" data-testid="create-toggle-password" onClick={() => setShowCreatePassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white transition-colors">
                  {showCreatePassword ? <EyeSlash weight="bold" className="w-5 h-5" /> : <Eye weight="bold" className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">Telefon</label>
              <Input data-testid="create-phone" value={createForm.phone} onChange={e => setCreateForm(p => ({ ...p, phone: e.target.value }))} placeholder="+905xx..." className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11" />
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">Rol</label>
              <Select value={createForm.role} onValueChange={v => setCreateForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="create-role-select" className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                  <SelectItem value="reporter" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Vatandaş</SelectItem>
                  <SelectItem value="officer" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Memur</SelectItem>
                  <SelectItem value="admin" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Yönetici</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {createForm.role === 'officer' && (
              <div>
                <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">Kurum</label>
                <Select value={createForm.institution_id} onValueChange={v => setCreateForm(p => ({ ...p, institution_id: v }))}>
                  <SelectTrigger data-testid="create-institution-select" className="bg-[#0A0A0A] border-[#2C2C2E] text-white h-11">
                    <SelectValue placeholder="Kurum seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                    {institutions.map(i => (
                      <SelectItem key={i.id} value={i.id} className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">{i.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="create-user-submit" onClick={handleCreate} disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white h-12 w-full font-heading uppercase">
              {saving ? 'Oluşturuluyor...' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
